import React, { useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/WhyUsPage.css';
import img1 from '../assets/whyUs-Image/img1.png';
import img2 from '../assets/whyUs-Image/img2.png';
import img3 from '../assets/whyUs-Image/img3.png';
import img4 from '../assets/whyUs-Image/img4.png';
import img5 from '../assets/whyUs-Image/img5.png';
import img6 from '../assets/whyUs-Image/img6.png';
import img7 from '../assets/whyUs-Image/img7.png';
import imageContainer from '../assets/whyUs-Image/img-container.png';
import '../styles/CTASection.css';
import ContactButton from './ContactButton';
import devopsIcon from '../assets/services/cicdIcon.svg';
import cloudIcon from '../assets/whyUs-Image/cloudIcon.svg';
import fullStackIcon from '../assets/whyUs-Image/fullStackIcon.svg';
import mobileIcon from '../assets/whyUs-Image/mobileIcon.svg';
import webIcon from '../assets/whyUs-Image/webIcon.svg';
import enterpriseIcon from '../assets/whyUs-Image/enterpriseIcon.svg';
import '../styles/OurServices.css';
import author1 from '../assets/whyUs-Image/client1.png';
import author2 from '../assets/whyUs-Image/client2.png';
import PageHeader from './PageHeader';

gsap.registerPlugin(ScrollTrigger);

const WhyUsPage: React.FC = () => {

  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && cardsRef.current) {
      const cards = gsap.utils.toArray('.cards') as HTMLElement[]; 
      
      gsap.to(cards, {
        xPercent: -110 * (cards.length - 1),
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          pin: true,
          scrub: 1,
          snap: 1 / (cards.length - 1),
          end: () => "+=" + (window.innerWidth * (cards.length - 1)),
          markers: false
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const whyUsCardsData = [
    {
      id: 1,
      imgSrc: img1,
      altText: "Disaster recovery illustration",
      title: "Instant \"Disaster-Proof\" Recovery",
      content: "When a critical failure strikes—say, your network configuration is accidentally erased—ReventLabs's recovery process rebuilds your entire environment in under an hour. No more frantically piecing together manual steps at 3 AM; you'll be back online before most of your team's morning coffee.",
    },
    {
      id: 2,
      imgSrc: img2, 
      altText: "Feature illustration",
      title: "Approve a Change, See It Live",
      content: "Instead of juggling manual updates and hoping nothing breaks, every infrastructure change goes through a simple review-and-approve flow. Once you greenlight it, our system applies the update automatically, so you can be confident that what's tested in staging looks the same in production.",
    },
    {
      id: 3,
      imgSrc: img3, 
      altText: "Feature illustration",
      title: "Pass Audits Before Breakfast",
      content: "Compliance shouldn't be a scramble. We embed validation checks into your processes so that every component meets your regulatory requirements before it exists. When auditors arrive, you'll hand over a clear report proving everything was vetted ahead of time—no scrambling or last-minute fixes.",
    },
    {
      id: 4,
      imgSrc: img4, 
      altText: "Feature illustration",
      title: "Effortless Scaling During Peak Demand",
      content: "Whether you're launching a new feature or running a limited-time promotion, your platform adjusts capacity automatically—no manual intervention. You'll stay online at your busiest moments, without worrying about sudden traffic spikes or performance hiccups.",
    },
    {
      id: 5,
      imgSrc: img5, 
      altText: "Feature illustration",
      title: "One Management Approach for All Clouds",
      content: "If you run workloads across multiple cloud providers, ReventLabs lets you handle everything consistently from a single framework. You decide which provider suits each task, and our solution ensures you don't have to re-create or reconfigure environments to make it happen.",
    },
    {
      id: 6,
      imgSrc: img6, 
      altText: "Feature illustration",
      title: "Free Your Engineers to Build, Not Break Stuff",
      content: "Your development team no longer needs to spend 70% of their time on routine upkeep—patching, updates, or environment provisioning. ReventLabs automates those mundane tasks behind the scenes, freeing your engineers to build features, not fight infrastructure fires.",
    },
    {
      id: 7,
      imgSrc: img7, 
      altText: "Feature illustration",
      title: "Sleep Soundly on Weekends",
      content: "When the workday ends—whether it's a Friday night or a holiday—you can relax knowing that any alert will trigger a proven response plan, not a frantic all-hands call. With ReventLabs's playbooks and training, your team handles incidents swiftly and confidently, so you actually get to enjoy your downtime.",
    }
  ];

  const testimonials = [
    {
      id: 1,
      text: `"ReventLabs transformed our outdated systems into a modern, scalable platform that has dramatically 
             improved our operational efficiency. Their technical expertise and client-focused approach made 
             them the perfect partner for our digital transformation journey."`,
      image: author1, 
      name: "John Doe",
      title: "CTO, Enterprise Sol. Inc."
    },
    {
      id: 2,
      text: `"The mobile application developed by ReventLabs has received outstanding feedback from our users. 
             Their development process was transparent, and they consistently delivered on time and within 
             budget. We couldn't be happier with the results."`,
      image: author2,
      name: "Ron Johnson",
      title: "Product Manager, TechInnovate"
    },
    {
      id: 3,
      text: `"ReventLabs's cloud migration strategy saved us significant operational costs while improving 
             our system's performance and reliability. Their team's deep expertise in cloud services and 
             DevOps has been invaluable to our business growth."`,
             initials: "RM",
      name: "Robert Miller",
      title: "CEO, Cloud Solutions Ltd"
    }
  ];

  
  const scroll = (direction: 'left' | 'right') => {
      if (scrollRef.current) {
        let scrollAmount;
        if (window.innerWidth <= 700) {
          scrollAmount = 400; 
        } else if (window.innerWidth >= 700 && window.innerWidth <= 900) {
          scrollAmount = 280;
        } else {
          scrollAmount = 650; 
        }
        const currentScrollLeft = scrollRef.current.scrollLeft;
        const newScrollLeft = direction === 'left' 
          ? currentScrollLeft - scrollAmount 
          : currentScrollLeft + scrollAmount;
        
        scrollRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        });
      }
    };
  
    const checkScrollPosition = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 10);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };
  
    useEffect(() => {
      checkScrollPosition();
    }, []);

  return (
    <div className="why-us-page">
      <PageHeader heading='Why Us?' text="In a world of generic solutions, ReventLabs stands apart. 
      We don’t just deliver technology—we deliver confidence. Our proven expertise, tailored strategies, 
      and relentless focus on your success ensure that every project exceeds expectations. Whether it’s 
      seamless scalability, ironclad security, or effortless compliance, we turn complex challenges into 
      effortless wins."/>

      {/* Metrics Section */}
      <div className="metrics-section-whyUS-Page">
        <div className="metrics-container">
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">97%</div>
            <div className="metrics-label">Client Satisfaction</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">150+</div>
            <div className="metrics-label">Projects Delivered</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">12+</div>
            <div className="metrics-label">Years Experience</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">24/7</div>
            <div className="metrics-label">Support Provided</div>
          </div>
          <div className="grid-divider"></div>
        </div>
      </div>

      
      <div className="why-us-container-why-page" ref={containerRef}>
        {/* Metrics Section */}
      <div className="metrics-section-new">
        <div className="metrics-container">
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">97%</div>
            <div className="metrics-label">Client Satisfaction</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">150+</div>
            <div className="metrics-label">Projects Delivered</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">12+</div>
            <div className="metrics-label">Years Experience</div>
          </div>
          <div className="grid-divider"></div>
          <div className="metric-item">
            <div className="metrics-value">24/7</div>
            <div className="metrics-label">Support Provided</div>
          </div>
          <div className="grid-divider"></div>
        </div>
      </div>
        <div className="why-us-cards" ref={cardsRef}>
          {whyUsCardsData.map((card) => (
            <div className='cards' key={card.id}>
              <img src={card.imgSrc} alt={card.altText} />
              <div className='why-us-card-side-content'>
                <p className='content-title'>{card.title}</p>
                <p className='content-text'>{card.content}</p>
                <div className="cta-new-button-wrapper">
                  <ContactButton variant="border">
                    Get Started
                  </ContactButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Our Process Section */}
      <div className="process-section">
        <div className='process'>
          <h2 className="why-us-section-title">Our Process</h2>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="step-title">Discovery</h3>
                <p className="step-description">
                  We begin by understanding your business, objectives, and challenges through collaborative 
                  workshops and in-depth discussions to ensure perfect alignment.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="step-title">Strategy & Planning</h3>
                <p className="step-description">
                  Our team develops a comprehensive roadmap, defining the scope, technology stack, architecture, 
                  and delivery timeline tailored to your specific requirements.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="step-title">Design & Development</h3>
                <p className="step-description">
                  Using agile methodologies, we bring your solution to life with iterative development cycles, 
                  regular check-ins, and continuous improvement based on feedback.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="step-title">Testing & Quality Assurance</h3>
                <p className="step-description">
                  Our rigorous QA process ensures your solution meets the highest standards of performance, 
                  security, and user experience before deployment.
                </p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">05</div>
              <div className="step-content">
                <h3 className="step-title">Deployment & Support</h3>
                <p className="step-description">
                  We handle seamless deployment and provide ongoing maintenance and support to ensure your 
                  solution continues to perform optimally and evolves with your needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='image-container'>
        <img src={imageContainer} alt="" />
      </div>

      <div className="tech-expertise-section">
        <div className='tech'>
          <div className='tech-header'>
            <h2 className="why-us-section-title">Our Technology Expertise</h2>
            <p className="tech-section-subtitle">Comprehensive solutions across the entire technology spectrum</p>
          </div>

          <div className="our-services-grid">
            {/* Cloud Migration Strategy */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={devopsIcon} alt="Cloud Migration" />
              </div>
              <h3 className="our-service-title">DevOps</h3>
              <p className="our-service-description">
                Streamline your development pipeline with our DevOps expertise. We implement CI/CD pipelines, infrastructure as code, and automated testing to accelerate delivery and improve reliability.
              </p>
            </div>

            {/* CI/CD Services */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={cloudIcon} alt="CI/CD Services" />
              </div>
              <h3 className="our-service-title">Cloud Services</h3>
              <p className="our-service-description">
                Leverage the full potential of AWS, Azure, and GCP with our cloud-native solutions. We design scalable, secure, and cost-effective cloud architectures tailored to your specific needs.
              </p>
            </div>

            {/* Cloud Compliance & Security */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={fullStackIcon} alt="Cloud Compliance & Security" />
              </div>
              <h3 className="our-service-title">Full Stack Development</h3>
              <p className="our-service-description">
                From frontend interfaces to backend systems, we deliver cohesive solutions using modern frameworks and languages. Our full stack expertise ensures smooth integration between all components.
              </p>
            </div>

            {/* Cloud Cost Optimization */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={mobileIcon} alt="Cloud Cost Optimization" />
              </div>
              <h3 className="our-service-title">Mobile Apps (iOS & Android)</h3>
              <p className="our-service-description">
                Create exceptional mobile experiences with our iOS and Android development services. We build native and cross-platform applications that are fast, intuitive, and feature-rich.
              </p>
            </div>

            {/* Cloud Cost Optimization */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={webIcon} alt="Cloud Cost Optimization" />
              </div>
              <h3 className="our-service-title">Web Applications</h3>
              <p className="our-service-description">
                Develop modern, responsive web applications that deliver exceptional user experiences. Our web development expertise spans React, Angular, Vue, and other leading frameworks.
              </p>
            </div>

            {/* Cloud Cost Optimization */}
            <div className="our-services-card">
              <div className="our-services-icon">
                <img src={enterpriseIcon} alt="Cloud Cost Optimization" />
              </div>
              <h3 className="our-service-title">Enterprise Solutions</h3>
              <p className="our-service-description">
                Build robust, scalable enterprise applications that drive business growth. Our solutions integrate seamlessly with your existing systems while providing advanced functionality.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="testimonials-section">
        <div className='clients'>
          <div className='clients-header'>
            <h2 className="why-us-section-title">What Our Clients Say</h2>
            <div className="industries-navigation-is">
              <button 
                className={`nav-button-is nav-button-left-is ${!canScrollLeft ? 'disabled' : ''}`}
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={`nav-button-is nav-button-right-is ${!canScrollRight ? 'disabled' : ''}`}
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
              >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          <div 
            className="testimonials-container" 
            ref={scrollRef}
            onScroll={checkScrollPosition}
          >
            <div 
              className="testimonials-track"
            >
              {testimonials.map((testimonial) => (
                <div className="testimonial" key={testimonial.id}>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.image ? (
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="author-avatar-img"
                        />
                      ) : (
                        <span>{testimonial.initials}</span>
                      )}
                    </div>
                    <div className="author-info">
                      <h4 className="author-name">{testimonial.name}</h4>
                      <p className="author-title">{testimonial.title}</p>
                    </div>
                  </div>
                  <div className="testimonial-content">
                    <p className="testimonial-text">
                      {testimonial.text}
                    </p>
                  </div>
                  
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};


export default WhyUsPage; 